import { ElementRef, inject, Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { MaybeNull, MaybeUndefined } from 'src/app/utils';
import { promiseWithCatch, subjectsComplete } from '../../../../../common/app.helpers';
import { MapLibraries, MapsAPILoader } from '../../../../../components/map/map.service';
import FieldAllowedValueClasses from '../../../field-allowed-value-classes.values';
import { parseGeocoderResponse, PreparedLocation } from './geocoding.helper';

@Injectable()
export class LocationInputAutocompleteService {
  private autocomplete: google.maps.places.Autocomplete;
  private autocompletePlaceChanged$: Subject<google.maps.places.PlaceResult> = new Subject();
  private placeResultParsed$: Subject<PreparedLocation> = new Subject();

  private readonly typesMapper: { [key: string]: string[] } = {
    [FieldAllowedValueClasses.TEXT_LOCATION]: ['(cities)'],
    [FieldAllowedValueClasses.CITY_LEVEL_LOCATION]: ['(cities)'],
    [FieldAllowedValueClasses.LOCATION]: ['address'],
    [FieldAllowedValueClasses.LOCATION_ALL_TYPES]: [],
  };

  private readonly resultProcessor: {
    [key: string]: (geocoderResult: google.maps.GeocoderResult) => void;
  } = {
    [FieldAllowedValueClasses.TEXT_LOCATION]: (geocoderResult: google.maps.GeocoderResult) =>
      this.processPlaceResultWithoutBuildingNumber(geocoderResult),
    [FieldAllowedValueClasses.CITY_LEVEL_LOCATION]: (geocoderResult: google.maps.GeocoderResult) =>
      this.processPlaceResultWithoutBuildingNumber(geocoderResult),
    [FieldAllowedValueClasses.LOCATION]: (geocoderResult: google.maps.GeocoderResult) =>
      this.processPlaceResultWithoutBuildingNumber(geocoderResult),
    [FieldAllowedValueClasses.LOCATION_ALL_TYPES]: (geocoderResult: google.maps.GeocoderResult) =>
      this.processPlaceResultWithoutBuildingNumber(geocoderResult),
    [FieldAllowedValueClasses.FULL_ADDRESS_LOCATION]: (geocoderResult: google.maps.GeocoderResult) =>
      this.processPlaceResultWithBuildingNumber(geocoderResult),
  };

  private mapsAPILoader = inject(MapsAPILoader);
  private ngZone = inject(NgZone);

  public ngOnDestroy(): void {
    subjectsComplete([this.autocompletePlaceChanged$, this.placeResultParsed$]);
  }

  public get autocompletePlaceChanged(): Observable<google.maps.places.PlaceResult> {
    return this.autocompletePlaceChanged$.asObservable();
  }

  public get placeResultParsed(): Observable<PreparedLocation> {
    return this.placeResultParsed$.asObservable();
  }

  public placesAutocompleteInit(elementRef: ElementRef<HTMLInputElement>, valueClass: string): void {
    promiseWithCatch(
      this.mapsAPILoader.load(MapLibraries.PLACES).then(() => {
        this.autocomplete = new google.maps.places.Autocomplete(elementRef.nativeElement);
        const types: MaybeUndefined<string[]> = this.typesMapper[valueClass];

        if (types) {
          this.autocomplete.setTypes(types);
        }

        this.autocomplete.setFields(['address_components', 'geometry', 'formatted_address']);
        this.searchPlace();
      }),
    );
  }

  public processPlaceResult(placeResult: google.maps.places.PlaceResult, value_class: string): void {
    const geocoder: google.maps.Geocoder = new google.maps.Geocoder();
    void geocoder.geocode(
      {
        address: placeResult.formatted_address,
        language: 'en',
      },
      (results: MaybeNull<google.maps.GeocoderResult[]>, status: google.maps.GeocoderStatus) => {
        if (status !== 'OK') {
          console.error(status);
          return;
        }

        if (!results) {
          console.error(status);
          return;
        }

        const result: MaybeUndefined<google.maps.GeocoderResult> = results[0];
        const handler: MaybeUndefined<(geocoderResult: google.maps.GeocoderResult) => void> =
          this.resultProcessor[value_class];

        if (!handler) {
          console.error(`No handler provided for ${value_class} field type`);
          return;
        }

        if (result) {
          handler(result);
        }
      },
    );
  }

  public processPlaceResultWithBuildingNumber(result: google.maps.GeocoderResult): void {
    const street: string = this.getStreetWithBuildingNumber(result.address_components);
    this.placeResultParsed$.next(
      parseGeocoderResponse({
        address_components: result.address_components,
        location: result.geometry.location,
        viewport: undefined,
        street: street,
      }),
    );
  }

  public processPlaceResultWithoutBuildingNumber(result: google.maps.GeocoderResult): void {
    const street: string = this.getStreet(result.address_components) ?? '';
    this.placeResultParsed$.next(
      parseGeocoderResponse({
        address_components: result.address_components,
        location: result.geometry.location,
        viewport: result.geometry.viewport,
        street,
      }),
    );
  }

  private getStreet(addressComponents: google.maps.GeocoderAddressComponent[]): MaybeUndefined<string> {
    return addressComponents.find((component: google.maps.GeocoderAddressComponent) => component.types[0] === 'route')
      ?.short_name;
  }

  private getStreetWithBuildingNumber(addressComponents: google.maps.GeocoderAddressComponent[]): string {
    const buildingNumber: string =
      addressComponents.find(
        (component: google.maps.GeocoderAddressComponent) => component.types[0] === 'street_number',
      )?.short_name ?? '';

    const street: string = this.getStreet(addressComponents) ?? '';

    return buildingNumber ? street + ' ' + buildingNumber : street;
  }

  private searchPlace(): void {
    this.autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place: google.maps.places.PlaceResult = this.autocomplete.getPlace();
        if (place.geometry) {
          this.autocompletePlaceChanged$.next(place);
        }
      });
    });
  }
}
